import { memo, useMemo } from 'react';
import PropTypes from 'prop-types';

import { chartAlpha, seriesColor } from '../constants/chartPalette';
import {
  ChartDataPoint,
  colourForToken,
  enterAt,
  formatRatio,
  formatValue,
  WidgetPeriodChip,
} from '../shared';

/** Bar height. A child's is shorter, so the nesting reads without indentation alone. */
const BAR_PX = 8;
const CHILD_BAR_PX = 5;

/**
 * Where the money went — `SPEND_BREAKDOWN`.
 *
 * ## Why this is not the pie chart
 *
 * It used to be: `spend_breakdown` resolved to `PieChartWidget` with
 * `donut: true`, on the reasoning that a breakdown is a composition and a
 * composition is a ring. The contract says `display.style: BAR`, and the
 * reason is structural rather than cosmetic — this type nests. A category
 * carries `children[]`, and **a ring cannot nest a sector**: there is no way
 * to show that «AutoPartes SAS» is part of «Parts» inside one circle without
 * inventing a second ring nobody asked for. Bars nest by indentation, which
 * is why every spend report that has ever existed uses them.
 *
 * ## How a row is read
 *
 * Length is the share of the **largest category**, not of the total. A
 * breakdown's top row would otherwise be the only one with any length in it:
 * at 61% of spend, the remaining three would be stubs. The share of the total
 * is printed, which is where a percentage belongs.
 *
 * A child's `pct` is its share **of its parent** — the contract computes it
 * that way, and it is what a reader wants: 48.8% of what we spend on parts
 * goes to one supplier. Its bar is scaled inside the parent's length for the
 * same reason.
 *
 * `is_others` keeps the neutral colour and italics everywhere, parent or
 * child: the tail of a top-N cut is not a category.
 *
 * @param {object} props
 * @param {object} props.data `SPEND_BREAKDOWN`'s `items[]`.
 * @param {string} [props.accent] Overrides the first category's colour.
 * @param {string} [props.className]
 */
function SpendBreakdownWidget({ data, accent, className = '' }) {
  const format = data?.currency_code
    ? { style: 'CURRENCY', currency_code: data.currency_code, ...(data.format ?? {}) }
    : data?.format ?? null;

  const items = useMemo(() => (data?.items ?? data?.slices ?? []).map((item, index) => ({
    key: item.key ?? item.label ?? String(index),
    label: item.label ?? item.key ?? '',
    value: item.value ?? 0,
    pct: item.pct,
    isOthers: Boolean(item.is_others),
    colour: item.color_token
      ? colourForToken(item.color_token, seriesColor(index, accent))
      : seriesColor(index, accent),
    children: (item.children ?? []).map((child, childIndex) => ({
      key: child.key ?? child.label ?? String(childIndex),
      label: child.label ?? '',
      value: child.value ?? 0,
      pct: child.pct,
      isOthers: Boolean(child.is_others),
    })),
  })), [accent, data]);

  if (!items.length) return null;

  const widest = Math.max(...items.map((item) => item.value), 0);
  const total = data?.total ?? items.reduce((sum, item) => sum + item.value, 0);
  const lengthOf = (value) => (widest > 0 ? (value / widest) * 100 : 0);
  const share = (item) => (
    typeof item.pct === 'number' ? formatRatio(item.pct, { signed: false, decimals: 1 }) : ''
  );

  return (
    <div className={`flex min-w-0 flex-col gap-1.5 ${className}`.trim()}>
      <div className="flex items-baseline gap-1.5">
        <span className="text-[10px] uppercase tracking-[0.04em] text-content-muted">Total</span>
        <span className="text-sm font-bold text-content">{formatValue(total, format)}</span>
      </div>

      <div className="flex min-w-0 flex-col gap-1.5">
        {items.map((item, index) => {
          const enter = enterAt(index, 'left');
          return (
            <div key={item.key} className="flex min-w-0 flex-col gap-1">
              <ChartDataPoint
                fill="block w-full"
                label={`${item.label} · ${formatValue(item.value, format)} — ${share(item)} of ${formatValue(total, format)}`}
              >
                <div className={`flex min-w-0 items-center gap-2 ${enter.className}`} style={enter.style}>
                  <span
                    className={`w-[84px] shrink-0 truncate text-[11px] ${
                      item.isOthers ? 'italic text-content-muted' : 'text-content'
                    }`}
                  >
                    {item.label}
                  </span>
                  <span className="min-w-0 flex-1 rounded-full bg-[rgb(var(--wg-wash))]" style={{ height: BAR_PX }}>
                    <span
                      className="block rounded-full"
                      style={{ width: `${lengthOf(item.value)}%`, height: BAR_PX, background: item.colour }}
                    />
                  </span>
                  <span className="w-[76px] shrink-0 text-right text-[11px] font-semibold text-content">
                    {formatValue(item.value, format)}
                  </span>
                  <span className="w-10 shrink-0 text-right text-[11px] text-content-muted">
                    {share(item)}
                  </span>
                </div>
              </ChartDataPoint>

              {/* Sub-categories, inside their parent's length. */}
              {item.children.map((child) => (
                <div key={child.key} className="flex min-w-0 items-center gap-2 pl-3">
                  <span
                    className={`w-[72px] shrink-0 truncate text-[10px] ${
                      child.isOthers ? 'italic text-content-faint' : 'text-content-muted'
                    }`}
                  >
                    {child.label}
                  </span>
                  <span className="min-w-0 flex-1" style={{ height: CHILD_BAR_PX }}>
                    <span
                      className="block rounded-full"
                      style={{
                        width: `${(lengthOf(item.value) * (child.pct ?? 0))}%`,
                        height: CHILD_BAR_PX,
                        background: chartAlpha(item.colour, '0.55'),
                      }}
                    />
                  </span>
                  <span className="w-[76px] shrink-0 text-right text-[10px] text-content-muted">
                    {formatValue(child.value, format)}
                  </span>
                  <span className="w-10 shrink-0 text-right text-[10px] text-content-faint">
                    {share(child)}
                  </span>
                </div>
              ))}
            </div>
          );
        })}
      </div>

      <WidgetPeriodChip period={data?.period} className="self-start" />
    </div>
  );
}

SpendBreakdownWidget.propTypes = {
  data: PropTypes.shape({
    items: PropTypes.arrayOf(PropTypes.shape({
      key: PropTypes.string,
      label: PropTypes.string,
      value: PropTypes.number,
      /** Share of the total for a category, of its parent for a child. */
      pct: PropTypes.number,
      is_others: PropTypes.bool,
      color_token: PropTypes.string,
      children: PropTypes.arrayOf(PropTypes.object),
    })),
    total: PropTypes.number,
    format: PropTypes.object,
    currency_code: PropTypes.string,
    period: PropTypes.object,
    /** Accepted so a `PIE_CHART` payload still draws. */
    slices: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  accent: PropTypes.string,
  className: PropTypes.string,
};

export default memo(SpendBreakdownWidget);
